export type ApproverQuestionAnswerType = 'text' | 'options';

export interface ApproverQuestion {
  id: string;
  prompt: string;
  answer_type: ApproverQuestionAnswerType;
  choices: string[];
  allow_multiple: boolean;
}

export interface ApproverQuestionAnswerRecord extends ApproverQuestion {
  answer?: string | null;
  selected_options: string[];
}

export interface ApproverQuestionAnswerInput {
  question_id: string;
  answer?: string;
  selected_options?: string[];
}

export const APPROVER_QUESTION_FIELD_TYPES = ['approver', 'contract_manager_approver'];

export const MAX_APPROVER_QUESTIONS = 20;

export const isApproverQuestionFieldType = (fieldType?: string): boolean =>
  !!fieldType && APPROVER_QUESTION_FIELD_TYPES.includes(fieldType);

export const createApproverQuestionId = (): string =>
  `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const createApproverQuestion = (): ApproverQuestion => ({
  id: createApproverQuestionId(),
  prompt: '',
  answer_type: 'text',
  choices: [],
  allow_multiple: false,
});

const normalizeChoices = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  const choices: string[] = [];
  raw.forEach((item) => {
    if (typeof item !== 'string') return;
    const choice = item.trim();
    if (choice && !choices.includes(choice)) {
      choices.push(choice);
    }
  });
  return choices;
};

const normalizeQuestion = (raw: any): ApproverQuestion | null => {
  if (!raw || typeof raw !== 'object') return null;
  const prompt = typeof raw.prompt === 'string' ? raw.prompt.trim() : '';
  if (!prompt) return null;
  const answerType: ApproverQuestionAnswerType = raw.answer_type === 'options' ? 'options' : 'text';
  const choices = answerType === 'options' ? normalizeChoices(raw.choices) : [];
  return {
    id: typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim() : createApproverQuestionId(),
    prompt,
    answer_type: answerType,
    choices,
    allow_multiple: answerType === 'options' && !!raw.allow_multiple,
  };
};

/** Aceita tanto o JSON persistido no campo quanto a lista já serializada pela API. */
export const parseApproverQuestions = (raw: unknown): ApproverQuestion[] => {
  if (!raw) return [];
  let value: unknown = raw;
  if (typeof value === 'string') {
    if (!value.trim()) return [];
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizeQuestion)
    .filter((question): question is ApproverQuestion => question !== null);
};

export const parseApproverAnswerRecords = (raw: unknown): ApproverQuestionAnswerRecord[] => {
  if (!raw) return [];
  let value: unknown = raw;
  if (typeof value === 'string') {
    if (!value.trim()) return [];
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  return value.reduce<ApproverQuestionAnswerRecord[]>((records, rawRecord: any) => {
    const question = normalizeQuestion(rawRecord);
    if (!question) return records;
    records.push({
      ...question,
      answer: typeof rawRecord?.answer === 'string' ? rawRecord.answer : null,
      selected_options: normalizeChoices(rawRecord?.selected_options),
    });
    return records;
  }, []);
};

export const serializeApproverQuestions = (questions: ApproverQuestion[]): string | undefined => {
  const normalized = questions
    .map((question) => ({
      id: question.id,
      prompt: question.prompt.trim(),
      answer_type: question.answer_type,
      choices: question.answer_type === 'options' ? normalizeChoices(question.choices) : [],
      allow_multiple: question.answer_type === 'options' && question.allow_multiple,
    }))
    .filter((question) => question.prompt);

  return normalized.length > 0 ? JSON.stringify(normalized) : undefined;
};

export const choicesToText = (choices: string[]): string => choices.join('\n');

export const textToChoices = (text: string): string[] =>
  normalizeChoices(text.split(/[\n,]/).map((choice) => choice.trim()));

/** Erro de preenchimento a exibir ao aprovador, ou null quando as respostas estão completas. */
export const validateApproverAnswers = (
  questions: ApproverQuestion[],
  answers: Record<string, ApproverQuestionAnswerInput>
): string | null => {
  for (const question of questions) {
    const answer = answers[question.id];
    if (question.answer_type === 'options') {
      const selected = answer?.selected_options || [];
      if (selected.length === 0) {
        return `Responda a pergunta "${question.prompt}".`;
      }
      if (!question.allow_multiple && selected.length > 1) {
        return `A pergunta "${question.prompt}" aceita apenas uma opção.`;
      }
    } else if (!answer?.answer || !answer.answer.trim()) {
      return `Responda a pergunta "${question.prompt}".`;
    }
  }
  return null;
};

export const buildApproverAnswersPayload = (
  questions: ApproverQuestion[],
  answers: Record<string, ApproverQuestionAnswerInput>
): ApproverQuestionAnswerInput[] =>
  questions.map((question) => {
    const answer = answers[question.id];
    if (question.answer_type === 'options') {
      return {
        question_id: question.id,
        selected_options: answer?.selected_options || [],
      };
    }
    return {
      question_id: question.id,
      answer: (answer?.answer || '').trim(),
    };
  });

export const formatApproverAnswer = (record: ApproverQuestionAnswerRecord): string => {
  if (record.answer_type === 'options') {
    return record.selected_options.join(', ');
  }
  return record.answer || '';
};
